// Flight search + natural-language engine, ported 1:1 from SkyKey source-template compute()
import { F } from "../data/aircraft";
import { P, INTL } from "../data/places";
import { STATS } from "../data/stats";

/* ---------- places helpers ---------- */
export const ALIAS = [];
for (const [name, v] of Object.entries(P)) {
  ALIAS.push([name.toLowerCase(), name]);
  for (const a of v[2]) ALIAS.push([a, name]);
}
ALIAS.sort((a, b) => b[0].length - a[0].length);

export function findPlace(s) {
  if (!s) return null;
  const q = s.trim().toLowerCase();
  if (!q) return null;
  for (const [a, n] of ALIAS) if (a === q) return n;
  for (const [a, n] of ALIAS)
    if (a.length > 3 && (q.startsWith(a) || (a.startsWith(q) && q.length >= 4))) return n;
  return null;
}

export function nm(a, b) {
  const R = 3440.065, t = Math.PI / 180, [la1, lo1] = P[a], [la2, lo2] = P[b];
  const h = Math.sin(((la2 - la1) * t) / 2) ** 2 +
    Math.cos(la1 * t) * Math.cos(la2 * t) * Math.sin(((lo2 - lo1) * t) / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/* ---------- natural language ---------- */
const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
const WORDNUM = { one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10, eleven: 11, twelve: 12, thirteen: 13, fourteen: 14, fifteen: 15, twenty: 20, thirty: 30, forty: 40, fifty: 50, sixty: 60, couple: 2 };
const TODAY = new Date(); TODAY.setHours(0, 0, 0, 0);

export function iso(d) { return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0"); }
export const TODAY_ISO = iso(TODAY);
function mkd(y, m, d) { const x = new Date(y, m, d); if (x.getMonth() !== m) return null; return x; }
function future(m, d, y) { let x = mkd(y ?? TODAY.getFullYear(), m, d); if (x && y == null && x < TODAY) x = mkd(TODAY.getFullYear() + 1, m, d); return x; }

function parseDate(t) {
  if (/\bday after tomorrow\b/.test(t)) { const d = new Date(TODAY); d.setDate(d.getDate() + 2); return iso(d); }
  if (/\btomorrow\b/.test(t)) { const d = new Date(TODAY); d.setDate(d.getDate() + 1); return iso(d); }
  if (/\btoday\b|\btonight\b/.test(t)) return iso(TODAY);
  let m = t.match(/\b(\d{4})-(\d{1,2})-(\d{1,2})\b/); if (m) { const d = mkd(+m[1], +m[2] - 1, +m[3]); if (d) return iso(d); }
  m = t.match(/\b(\d{1,2})[\/.\-](\d{1,2})(?:[\/.\-](\d{2,4}))?\b/);
  if (m && +m[2] <= 12) { const y = m[3] ? (m[3].length === 2 ? 2000 + +m[3] : +m[3]) : null; const d = future(+m[2] - 1, +m[1], y); if (d) return iso(d); }
  const mon = "(jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)[a-z]*";
  m = t.match(new RegExp("\\b(\\d{1,2})(?:st|nd|rd|th)?\\s+(?:of\\s+)?" + mon + "(?:\\s+(\\d{4}))?"));
  if (m) { const d = future(MONTHS.indexOf(m[2].slice(0, 3)), +m[1], m[3] ? +m[3] : null); if (d) return iso(d); }
  m = t.match(new RegExp("\\b" + mon + "\\s+(\\d{1,2})(?:st|nd|rd|th)?(?:,?\\s+(\\d{4}))?"));
  if (m) { const d = future(MONTHS.indexOf(m[1].slice(0, 3)), +m[2], m[3] ? +m[3] : null); if (d) return iso(d); }
  return "";
}

export function parse(text) {
  const t = " " + text.toLowerCase().replace(/[,;!?]/g, " ") + " ";
  const hits = [];
  for (const [a, n] of ALIAS) {
    const re = new RegExp("(^|[^a-z])" + a.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "(?=[^a-z]|$)", "g");
    let m;
    while ((m = re.exec(t))) { const i = m.index + m[1].length; if (!hits.some((h) => i < h.i + h.len && i + a.length > h.i)) hits.push({ i, len: a.length, n }); }
  }
  hits.sort((x, y) => x.i - y.i);
  const seq = hits.map((h) => h.n).filter((n, i, a) => i === 0 || n !== a[i - 1]);
  let from = seq[0] || null, to = seq[1] || null;
  const toIdx = t.search(/\sto\s/), fromIdx = t.search(/\sfrom\s/);
  if (seq.length >= 2 && fromIdx > toIdx && toIdx >= 0) { const h1 = hits.find((h) => h.i > toIdx), h2 = hits.find((h) => h.i > fromIdx); if (h1 && h2) { to = h1.n; from = h2.n; } }
  let pax = null, m = t.match(/(\d{1,3})\s*(?:pax|passengers?|people|persons?|guests?|adults?|members?|travell?ers?|seats?|ppl|of us|executives?)/);
  if (!m) m = t.match(/(?:with|for|carry|take)\s+(\d{1,3})\b(?!\s*(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec|st|nd|rd|th))/);
  if (m) pax = +m[1];
  if (!pax) { for (const [w, v] of Object.entries(WORDNUM)) { if (new RegExp("\\b" + w + "\\s+(pax|passengers?|people|persons?|guests?|adults?|of us)").test(t)) { pax = v; break; } } }
  if (!pax && /\b(me and my wife|couple)\b/.test(t)) pax = 2;
  const mode = /\b(heli|helicopter|chopper|helipad)\b/.test(t) ? "heli" : /\b(cargo|freight|tonnes?|shipment|goods)\b/.test(t) ? "cargo" : /\bjet\b/.test(t) ? "jet" : "";
  return { from, to, pax, date: parseDate(t), mode, med: /\b(ambulance|medical|patient|stretcher|icu)\b/.test(t), lux: /\b(luxury|vip|premium|bedroom)\b/.test(t), ret: /\b(return|round ?trip|and back)\b/.test(t) };
}

/* ---------- search engine ---------- */
export function fmtH(h) { let H = Math.floor(h), M = Math.round((h - H) * 60 / 5) * 5; if (M === 60) { H++; M = 0; } return H ? (M ? `${H}h ${M}m` : `${H}h`) : `${M}m`; }

export function compute(S) {
  const notes = [], out = [];
  if (S.from === S.to) return { notes: [{ c: "warn", t: "Departure and destination are the same. For a local sightseeing flight, look at our helicopters below." }], res: [], dist: 0 };
  const dist = nm(S.from, S.to) * 1.07, heliOnly = P[S.from][3] || P[S.to][3], pax = Math.max(1, S.pax || 1);
  if (heliOnly) notes.push({ c: "warn", t: `${P[S.from][3] ? S.from : S.to} has no airport, so we're showing helicopters only. Pilgrimage routes may follow state-authorised shuttle schedules; we'll confirm what's possible.` });
  if (INTL.has(S.from) || INTL.has(S.to)) notes.push({ c: "", t: "International trip: we arrange foreign landing and overflight permits. Passengers need valid passports and visas, so please allow extra lead time." });
  if (S.med) notes.push({ c: "", t: "Medical flight: aircraft commonly fitted for stretchers are listed first. Tell us the patient's condition in your request so we can arrange the right medical crew." });
  for (const [fk, f] of Object.entries(F)) {
    const st = STATS[fk]; if (!st || !st.n) continue;
    if (S.mode === "cargo") { if (f.use !== "cargo") continue; } else { if (f.use) continue; if (st.mx < pax) continue; }
    const isHeli = f.k === "heli";
    if (S.mode === "heli" && !isHeli) continue; if (S.mode === "jet" && f.k !== "jet") continue; if (heliOnly && !isHeli) continue;
    const load = S.mode === "cargo" ? 0.5 : Math.min(1, pax / Math.max(st.mx, 1)), eff = f.r * (0.92 - 0.18 * load);
    let stops = 0;
    if (isHeli) { if (!(S.mode === "heli" || heliOnly || dist <= 220)) continue; if (dist > eff * 0.85) { if (dist <= eff * 1.7) stops = 1; else continue; } }
    else { if (dist > eff) { const lh = ["mid", "supermid", "large", "ulr", "bizliner"].includes(f.c); if (dist <= eff * 2.1 && (f.k === "jet" || dist <= 1400)) stops = 1; else if (lh && dist <= eff * 3.1 && dist > 3000) stops = 2; else continue; } }
    const block = dist / f.s + (isHeli ? 0.2 : 0.35) + stops * 0.9;
    let score = f.t * block * (1 + 0.35 * stops);
    if (!f.pr && !isHeli) score *= 1.2; if (f.k === "piston") score *= 1.9;
    if (S.med && ["B200", "PC12", "KA350", "LJ45", "EC135", "H145", "C90", "CITXL"].includes(fk)) score *= 0.55;
    if (S.lux) score = -f.t;
    if (dist < 120 && f.k === "jet") score *= 1.4;
    if (!S.lux && ["large", "ulr", "bizliner"].includes(f.c) && dist < 1500 && pax <= 9) score *= 1.6;
    out.push({ f: fk, stops, block, score });
  }
  out.sort((a, b) => a.score - b.score);
  if (out.length) {
    out[0].badge = S.lux ? "Most luxurious" : S.med ? "Medical pick" : "Best value";
    const fast = [...out].sort((a, b) => a.block - b.block)[0]; if (fast !== out[0] && !fast.badge) fast.badge = "Fastest";
    const big = [...out].filter((g) => !g.badge).sort((a, b) => F[b.f].t - F[a.f].t)[0]; if (big && out.length > 4 && !big.badge) big.badge = "Most spacious";
  } else notes.push({ c: "bad", t: heliOnly ? "No single helicopter seats this group. We can split the group across several helicopters, so send us a request." : "No single aircraft fits this group and route. We can split the group across aircraft or suggest a group charter, so send us a request." });
  return { notes, res: out, dist };
}

/* ---------- display helpers ---------- */
export function tier(t) { const n = t < 2.5 ? 1 : t < 5 ? 2 : t < 8 ? 3 : t < 13 ? 4 : 5; return { fill: n, empty: 5 - n }; }
export function seatsTxt(fk) { const st = STATS[fk], f = F[fk]; if (f.use === "cargo") return `About ${f.cap} tonnes`; if (!st || !st.mx) return "\u2014"; return `Up to ${st.mx} seats`; }
export function slugify(fk) { return F[fk].n.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, ""); }
